import React from 'react';

import CardDialog from '~/components/CardDialog';
import LoadingSwitch from '~/components/core/Loading/LoadingSwitch';
import StatusChip from '~/components/core/StatusChip';
import SortableTable from '~/components/core/Tables/SortableTable';
import Text from '~/components/core/TextComponents/Text';
import { serverDateTimeToLocal } from '~/DateTimeUtils';

import { STATUS_TO_CHIP } from './constants';
import type { AiClaimImportRunProgress, StatusBreakdown } from './types';

interface RunProgressDialogProps {
  open: boolean;
  onClose: () => void;
  progress: AiClaimImportRunProgress | null;
  isLoading: boolean;
}

interface BreakdownRow {
  id: number;
  type: string;
  completed: number;
  processing: number;
  pending: number;
  failed: number;
  total: number;
}

const buildRow = (id: number, type: string, breakdown: StatusBreakdown): BreakdownRow => ({
  id,
  type,
  completed: breakdown.completed,
  processing: breakdown.processing,
  pending: breakdown.pending,
  failed: breakdown.failed,
  total: breakdown.total,
});

const columns = [
  { id: 'type', label: 'Type', disableSort: true },
  { id: 'completed', label: 'Processed Successfully', disableSort: true, numeric: true },
  { id: 'processing', label: 'In Progress', disableSort: true, numeric: true },
  { id: 'pending', label: 'Not Started', disableSort: true, numeric: true },
  { id: 'failed', label: 'Failed', disableSort: true, numeric: true },
  { id: 'total', label: 'Total', disableSort: true, numeric: true },
];

const RunProgressDialog: React.FC<RunProgressDialogProps> = ({ open, onClose, progress, isLoading }) => {
  const rows: BreakdownRow[] = progress
    ? [
        buildRow(1, 'Claims', progress.claims),
        buildRow(2, 'Documents', progress.documents),
        buildRow(3, 'Communications', progress.communications),
        buildRow(4, 'Notes', progress.notes),
      ]
    : [];

  return (
    <CardDialog isDialog open={open} onClose={onClose} title="Run Progress Details" maxWidth="md" fullWidth>
      <LoadingSwitch isLoading={isLoading} isError={false}>
        {progress && (
          <div className="flex flex-col gap-16 p-16">
            <div className="flex flex-wrap items-center gap-16">
              <Text variant={Text.VARIANTS.XS} colorVariant={Text.COLOR_VARIANTS.SECONDARY}>
                Run ID: {progress.run_id}
              </Text>
              <Text variant={Text.VARIANTS.XS} colorVariant={Text.COLOR_VARIANTS.SECONDARY}>
                Started: {serverDateTimeToLocal(progress.created_at)}
              </Text>
              <Text variant={Text.VARIANTS.XS} colorVariant={Text.COLOR_VARIANTS.SECONDARY}>
                By: {progress.initiated_by_user_name}
              </Text>
              <StatusChip status={STATUS_TO_CHIP[progress.status] || StatusChip.STATUSES.PENDING} />
            </div>
            <SortableTable columns={columns} rows={rows} keepRowsOrder />
          </div>
        )}
      </LoadingSwitch>
    </CardDialog>
  );
};

export default RunProgressDialog;
